import { cloneElement, isValidElement } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";

import {
  FACT_CITATION_RE,
  FACT_CITATION_WITH_LEADING_SPACE_RE,
  isRenderableFactId,
  stripKeepInMindSection,
} from "./analystFactNumbering";

import type { ReactNode } from "react";
import type { FactNumbering } from "./analystFactNumbering";

interface SafeAnalystMarkdownProps {
  content: string;
  numbering: FactNumbering;
  onOpenFact: (factId: string) => void;
  /**
   * Refused turns render as plain prose in the warning color, never as
   * markdown, mirroring the `refused` branch of
   * pages/ThreadDetailPage/ThreadAnswerBody.tsx.
   *
   * This has to live HERE rather than as a color class on the caller's
   * wrapper: the wrapper below hard-sets `text-foreground`, and so does
   * `HEADING_BASE`, so an ancestor's `text-chart-warning` loses the cascade
   * and a refusal renders in ordinary body color.
   */
  refused?: boolean;
  /**
   * Hides table nodes when the caller already renders the same data as an
   * `AnalystTable`, avoiding a duplicate table in the prose (from dev's
   * SafeAnalystMarkdown; preserved across the analyst-thread-alignment
   * refactor).
   */
  hideMarkdownTables?: boolean;
}

// Thread heading language, copied from `HEADING_BASE` in
// pages/ThreadDetailPage/ThreadAnswerBody.tsx (not imported: per-tab tokens
// only, see the plan's "share tokens, not modules" decision). globals.css
// wires h1-h3 to `--font-display` (Sumana) for page titles, so a heading
// sitting inside generated prose must opt back out to `font-sans` explicitly.
// Sumana is never bold, so weight lives on `font-sans` here, not on a display
// face.
const HEADING_BASE = "font-sans font-semibold text-foreground first:mt-0";

// Circle chip language, copied from `CITATION_MARKER_CHIP_CLASS` in
// pages/ThreadDetailPage/ThreadCitationMarker.tsx (not imported, same reason
// as HEADING_BASE above).
const FACT_CHIP_CLASS =
  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none bg-primary/10 text-foreground hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";

/**
 * Removes every `[fact-…]` marker that has no matching entry in `numbering`,
 * along with its leading whitespace, before the content ever reaches
 * `ReactMarkdown`. This runs at the string level (not inside the per-node
 * walk below) so it also reaches a marker that lands inside a heading or any
 * other node the walk does not visit, matching the blanket strip this tab has
 * always done for unresolvable ids. A resolvable marker's full match
 * (whitespace included) is returned unchanged, so paragraph structure is
 * untouched and the per-node walk below can assume anything still matching
 * `FACT_CITATION_RE` is safe to render as a chip.
 */
function stripUnresolvableCitations(
  content: string,
  numbering: FactNumbering,
): string {
  const withSpacePattern = new RegExp(
    FACT_CITATION_WITH_LEADING_SPACE_RE.source,
    "g",
  );
  const bareCitationPattern = new RegExp(FACT_CITATION_RE.source);

  return content.replace(withSpacePattern, (fullMatch) => {
    const idMatch = bareCitationPattern.exec(fullMatch);
    const factId = idMatch?.[1];
    if (factId && isRenderableFactId(factId, numbering)) {
      return fullMatch;
    }
    return "";
  });
}

/**
 * Inline code. Hoisted to a named component (rather than an inline arrow in the
 * `components` map) so `renderFactChipChildren` can identify it by reference
 * and refuse to walk into it.
 */
function InlineCode(props: { children?: ReactNode }) {
  const { children } = props;

  return (
    <code className="rounded bg-muted px-1 py-0.5 text-[0.9em]">{children}</code>
  );
}

interface FactChipProps {
  factId: string;
  number: number;
  onOpenFact: (factId: string) => void;
}

function FactChip(props: FactChipProps) {
  const { factId, number, onOpenFact } = props;
  const { t } = useTranslation("app");

  return (
    <button
      type="button"
      data-testid={`analyst-fact-chip-${factId}`}
      aria-label={t("studyReport.analyst.factChipAria", { index: number })}
      className={FACT_CHIP_CLASS}
      onClick={() => onOpenFact(factId)}
    >
      {number}
    </button>
  );
}

/**
 * Splits one string node on `FACT_CITATION_RE` and substitutes a `FactChip`
 * for each match. By the time this runs, `stripUnresolvableCitations` has
 * already removed every marker that is not renderable, so every match here is
 * expected to resolve; `isRenderableFactId` is still checked as a defensive
 * guard rather than an assumption.
 */
function splitFactCitations(
  text: string,
  numbering: FactNumbering,
  onOpenFact: (factId: string) => void,
  keyPrefix: string,
): ReactNode {
  const citationPattern = new RegExp(FACT_CITATION_RE.source, "g");
  const parts: Array<ReactNode> = [];
  let cursor = 0;
  let chipIndex = 0;
  let lastNumber: number | null = null;
  let match: RegExpExecArray | null;

  while ((match = citationPattern.exec(text)) !== null) {
    const factId = match[1];
    if (!isRenderableFactId(factId, numbering)) {
      continue;
    }

    const number = numbering.numberByFactId.get(factId) as number;
    const matchStart = match.index;
    const gap = text.slice(cursor, matchStart);
    // A breakdown cites every bucket. Adjacent markers that share a source
    // number are one chip; a later mention in another sentence still shows.
    if (lastNumber === number && gap.trim() === "") {
      cursor = citationPattern.lastIndex;
      continue;
    }
    if (matchStart > cursor) {
      parts.push(gap);
    }
    parts.push(
      <FactChip
        key={`${keyPrefix}-fact-${chipIndex}`}
        factId={factId}
        number={number}
        onOpenFact={onOpenFact}
      />,
    );
    lastNumber = number;
    cursor = citationPattern.lastIndex;
    chipIndex += 1;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1 && typeof parts[0] === "string") {
    return parts[0];
  }
  return parts;
}

/**
 * Walks the React children of a text-bearing renderer looking for fact
 * citations in string nodes. Recurses into any child element that itself
 * carries a `children` prop, so a marker sitting inside `<strong>` inside
 * `<li>` is still found and substituted. This never touches the `a` or `img`
 * renderers below, which stay untouched security controls.
 */
function renderFactChipChildren(
  children: ReactNode,
  numbering: FactNumbering,
  onOpenFact: (factId: string) => void,
  keyPrefix: string,
): ReactNode {
  if (Array.isArray(children)) {
    return children.map((child, index) =>
      renderFactChipChildren(
        child,
        numbering,
        onOpenFact,
        `${keyPrefix}-${index}`,
      ),
    );
  }

  if (typeof children === "string") {
    return splitFactCitations(children, numbering, onOpenFact, keyPrefix);
  }

  if (isValidElement(children)) {
    // Never descend into code. An inline `[fact-…]` written inside backticks is
    // literal text the author meant to show, not a citation, and turning it
    // into a button rewrites the code sample. Block code is already safe (the
    // `pre` renderer never walks), so this covers the inline case.
    if (children.type === InlineCode) {
      return children;
    }
    const elementProps = children.props as { children?: ReactNode };
    if (elementProps && "children" in elementProps) {
      return cloneElement(children, {
        children: renderFactChipChildren(
          elementProps.children,
          numbering,
          onOpenFact,
          keyPrefix,
        ),
      });
    }
    return children;
  }

  return children;
}

function hideTableNode(): null {
  return null;
}

export function SafeAnalystMarkdown(props: SafeAnalystMarkdownProps) {
  const {
    content,
    numbering,
    onOpenFact,
    refused = false,
    hideMarkdownTables = false,
  } = props;

  const withChips = (children: ReactNode, keyPrefix: string): ReactNode =>
    renderFactChipChildren(children, numbering, onOpenFact, keyPrefix);

  const displayContent = stripUnresolvableCitations(
    stripKeepInMindSection(content),
    numbering,
  );

  if (refused) {
    return (
      <div
        className="max-w-prose text-base leading-[1.7] text-chart-warning"
        data-testid="analyst-refusal-body"
      >
        {content}
      </div>
    );
  }

  return (
    <div className="max-w-prose text-base leading-[1.7] text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          a: ({ children }) => <span>{children}</span>,
          img: ({ alt }) => <span>{alt || "Image"}</span>,
          h1: ({ children }) => (
            <h1 className={`mb-2 mt-10 text-lg leading-tight ${HEADING_BASE}`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              className={`mb-2 mt-10 text-base leading-snug ${HEADING_BASE}`}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              className={`mb-1 mt-9 text-[15px] leading-snug ${HEADING_BASE}`}
            >
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4
              className={`mb-1 mt-6 text-base leading-snug ${HEADING_BASE}`}
            >
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="my-4 first:mt-0 last:mb-0">
              {withChips(children, "p")}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">
              {withChips(children, "strong")}
            </strong>
          ),
          em: ({ children }) => <em>{withChips(children, "em")}</em>,
          ul: ({ children }) => (
            <ul className="my-4 ml-5 list-disc space-y-1 first:mt-0 last:mb-0">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-4 ml-5 list-decimal space-y-1 first:mt-0 last:mb-0">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="my-1 leading-[1.7]">{withChips(children, "li")}</li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-6 border-l-2 border-border/60 pl-4 italic text-muted-foreground">
              {withChips(children, "blockquote")}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="my-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="border-b border-border">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-border/30 last:border-0">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 text-left font-medium">
              {withChips(children, "th")}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 align-top">
              {withChips(children, "td")}
            </td>
          ),
          code: InlineCode,
          pre: ({ children }) => (
            <pre className="my-6 overflow-x-auto rounded-md bg-muted p-4 text-[0.9em] [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit">
              {children}
            </pre>
          ),
          ...(hideMarkdownTables
            ? {
                table: hideTableNode,
                thead: hideTableNode,
                tbody: hideTableNode,
                tr: hideTableNode,
                th: hideTableNode,
                td: hideTableNode,
              }
            : {}),
        }}
        urlTransform={() => ""}
      >
        {displayContent}
      </ReactMarkdown>
    </div>
  );
}
